import React, { useState, useEffect, useRef } from 'react'
import { Container, Typography, Divider, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import MyTeamPage from 'components/templates/navigationPageTemplates/MyTeamPage'
import moment from 'moment'
import LiveNote from 'components/molecules/myTeam/LiveNote'
import InCardStack from 'components/atoms/container/InCardStack'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getLocalUser } from 'utils/localStorage'
import {
  getCalendarEvent,
  getConnectionEvent,
  patchUserCalendarEvent
} from 'services/userConnectionsHelper'
import { MEETINGS } from 'routes/constants'
import colors from 'styles/colors'
import UserMeetingHeader from 'components/molecules/myTeam/UserMeetingHeader'
import CheckInSurveyResult from 'components/organisms/myTeam/CheckInSurveyResult'
import { eventEnded } from 'utils/eventUtils'
import { Tag } from 'components/atoms/Text'
import RowStack from 'components/atoms/container/RowStack'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import MeetingSummary from 'components/organisms/myTeam/MeetingSummary'
import NoteTakerBanner from 'components/molecules/myTeam/meeting/NoteTakerBanner'
import { getThirdPartyOauth } from 'services/userServices'
import { track1on1MeetingAttrs } from 'services/amplitudeMeetingTracking'
import { FEATURE_KEYS } from 'services/constants'
import { isAllowedFeature } from 'utils/permissionCheck'

const UserMeeting = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const userConnectionEventId = location?.state?.userConnectionEventId
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [userCalendarEventId, setUserCalendarEventId] = useState(
    location?.state?.userCalendarEventId
  )
  const [event, setEvent] = useState(null)
  const [connection, setConnection] = useState(null)
  const [surveyUser, setSurveyUser] = useState(null)
  const userSharedNotes = useRef('')
  const userConnectionSharedNotes = useRef('')
  const privateNotes = useRef('')
  const [isClosed, setIsClosed] = useState(null)
  const [isOwner, setIsOwner] = useState(false)
  const [hasSummary, setHasSummary] = useState(false)
  const [hasBot, setHasBot] = useState(false)
  const [meetingUrl, setMeetingUrl] = useState(null)
  const [calendarNotConnected, setCalendarNotConnected] = useState(false)
  const hasLicense = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)

  const setMeetingAttrs = event => {
    setEvent(event)
    setIsClosed(event.isClosed)
    setHasBot(event?.botId)
    setMeetingUrl(event?.meetingUrl)
  }

  const handleDataRefresh = (updatedEvent, updateMeetingAttrs = true) => {
    if (updateMeetingAttrs) setMeetingAttrs(updatedEvent)
    if (isOwner) {
      userConnectionSharedNotes.current =
        updatedEvent.userConnectionSharedNotes ?? ''
    } else {
      userSharedNotes.current = updatedEvent?.userSharedNotes ?? ''
      privateNotes.current = updatedEvent?.notes ?? ''
    }
  }

  const checkCalenderLinked = async () => {
    const res = await getThirdPartyOauth()
    setCalendarNotConnected(res?.status !== 200)
  }

  const fetchData = async fetchingCalendarEventId => {
    setIsLoading(true)
    let calendarEvent
    let connectionEvent
    if (fetchingCalendarEventId) {
      calendarEvent = await getCalendarEvent(fetchingCalendarEventId)
      if (!calendarEvent) {
        navigate(MEETINGS)
        return
      }
      const connectionEId = calendarEvent.userConnectionEventId
      connectionEvent = await getConnectionEvent(connectionEId)
    } else {
      connectionEvent = await getConnectionEvent(userConnectionEventId)
      if (!connectionEvent) {
        navigate(MEETINGS)
        return
      }
      const futureEvents = connectionEvent.userCalendarEvents
        .filter(e => moment.utc(e.endTime).isAfter())
        .sort((e1, e2) => {
          return moment.utc(e1.startTime).diff(moment.utc(e2.startTime))
        })
      const calendarEId =
        futureEvents[0]?.id || connectionEvent.userCalendarEvents[0]?.id
      calendarEvent = await getCalendarEvent(calendarEId)
      setUserCalendarEventId(calendarEvent.id)
    }

    // Fallback to connection if userConnection is not available
    let connection = connectionEvent.userConnection.connection
    if (!connection) {
      connection = connectionEvent.userConnection
    }

    const ownedEvent =
      getLocalUser().id === connectionEvent.userConnection.createdByUserId
    setIsOwner(ownedEvent)
    setConnection(
      ownedEvent ? connection : connectionEvent.userConnection.connectedBy
    )
    setSurveyUser(connection)
    userSharedNotes.current = calendarEvent.userSharedNotes ?? ''
    privateNotes.current = calendarEvent.notes ?? ''
    userConnectionSharedNotes.current =
      calendarEvent.userConnectionSharedNotes ?? ''
    setMeetingAttrs(calendarEvent)
    setIsLoading(false)
  }
  useEffect(() => {
    if (!userCalendarEventId && !userConnectionEventId) {
      navigate(MEETINGS)
    } else {
      fetchData(userCalendarEventId)
      checkCalenderLinked()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      location.state?.userCalendarEventId &&
      location.state?.userCalendarEventId !== userCalendarEventId
    ) {
      setUserCalendarEventId(location.state.userCalendarEventId)
      fetchData(location.state.userCalendarEventId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state])

  useEffect(() => {
    const intervalId = setInterval(async () => {
      // Only do live update if event started and not closed
      if (event && !isClosed && moment.utc(event.startTime).isBefore()) {
        const updatedEvent = await getCalendarEvent(userCalendarEventId)
        if (updatedEvent) {
          handleDataRefresh(updatedEvent)
        }
      }
    }, 10000)
    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, isClosed])

  let patchController = null
  const handleUpdateEvent = async (field, value) => {
    // Abort previous request
    if (patchController) patchController.abort()

    // Create new controller
    patchController = new AbortController()

    const updated = await patchUserCalendarEvent(
      userCalendarEventId,
      {
        [field]: value
      },
      {
        signal: patchController.signal
      }
    )
    if (updated) {
      if (!event[field] && value) {
        track1on1MeetingAttrs('Meeting notes taken', event, {
          role: isOwner
            ? 'Leader'
            : event?.userConnectionEvent?.userConnection?.relationship,
          noteType: field === 'notes' ? 'private' : field
        })
      }
      event[field] = value
      handleDataRefresh(updated, false)
    }
    return updated
  }

  useEffect(() => {
    const hasCurrentSummary = !!event?.summary || !!event?.transcript
    setHasSummary(hasCurrentSummary || hasBot)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBot])

  const boxBorderSx = {
    borderRadius: 2,
    border: `1px solid ${colors.Grey200}`,
    backgroundColor: colors.White,
    overflow: 'hidden'
  }
  return (
    <MyTeamPage customizedBodyWidth>
      <OverlayLoading open={isLoading} />
      {event && !isLoading && (
        <Container maxWidth='xl' sx={{ height: '100%' }}>
          <InCardStack
            spacing={12}
            sx={{
              paddingX: 5,
              alignItems: 'flex-start',
              height: '100%'
            }}>
            <UserMeetingHeader
              connection={connection}
              event={event}
              isOwner={isOwner}
              isClosed={isClosed}
              meetingUrl={meetingUrl}
              setMeetingUrl={setMeetingUrl}
              hasBot={hasBot}
              setHasBot={setHasBot}
              calendarNotConnected={calendarNotConnected}
              setCalendarNotConnected={setCalendarNotConnected}
            />

            <Grid
              container
              paddingX={5}
              paddingBottom={8}
              spacing={12}
              width={'100%'}
              height={'calc(100% - 168px)'}>
              <Grid size={hasSummary ? 4.5 : 9} height={'100%'}>
                <InCardStack
                  spacing={8}
                  sx={{
                    ...boxBorderSx,
                    padding: 8,
                    width: '100%',
                    alignItems: 'flex-start',
                    height: '100%'
                  }}>
                  <Tag
                    sx={{
                      padding: 2,
                      ...theme.typography.h5,
                      background: eventEnded(event)
                        ? colors.Peach200
                        : colors.GoldenYellow100
                    }}>
                    {eventEnded(event)
                      ? t('myTeam.meetingHasFinished')
                      : t('myTeam.prepareForMeeting')}
                  </Tag>

                  <InCardStack
                    spacing={8}
                    sx={{
                      width: '100%',
                      alignItems: 'flex-start',
                      height: '100%',
                      overflowY: 'auto'
                    }}>
                    <LiveNote
                      isClosed={isClosed}
                      connection={connection}
                      notes={
                        isOwner ? userSharedNotes : userConnectionSharedNotes
                      }
                      handleUpdateNotes={n =>
                        handleUpdateEvent(
                          isOwner
                            ? 'userSharedNotes'
                            : 'userConnectionSharedNotes',
                          n
                        )
                      }
                    />
                    <Divider
                      sx={{
                        borderColor: colors.Grey200,
                        width: '100%'
                      }}
                    />
                    {/* Notes preview for the other connection */}
                    <LiveNote
                      isClosed={isClosed}
                      connection={connection}
                      notes={
                        !isOwner ? userSharedNotes : userConnectionSharedNotes
                      }
                      showPreview={true}
                    />

                    {hasLicense && (
                      <NoteTakerBanner
                        event={event}
                        meetingUrl={meetingUrl}
                        setMeetingUrl={setMeetingUrl}
                        hasBot={hasBot}
                        setHasBot={setHasBot}
                        eventEnded={eventEnded(event, 0)}
                        calendarNotConnected={calendarNotConnected}
                        setCalendarNotConnected={setCalendarNotConnected}
                      />
                    )}
                  </InCardStack>
                </InCardStack>
              </Grid>

              {hasSummary && (
                <Grid size={4.5} height={'100%'}>
                  <MeetingSummary event={event} connection={connection} />
                </Grid>
              )}

              <Grid
                container
                size={3}
                rowSpacing={12}
                direction='column'
                flexWrap={'nowrap'}
                height={'100%'}>
                <Grid>
                  <CheckInSurveyResult
                    isOwner={isOwner}
                    userCalendarEvent={event}
                    surveyUser={surveyUser}
                  />
                </Grid>

                {isOwner && (
                  <Grid sx={{ flexGrow: 1, minHeight: 250 }}>
                    <InCardStack
                      sx={{
                        ...boxBorderSx,
                        height: '100%',
                        alignItems: 'flex-start'
                      }}
                      spacing={0}>
                      <RowStack spacing={4} padding={8}>
                        <StickyNote2OutlinedIcon
                          sx={{ fontSize: 20, color: colors.MidnightTeal400 }}
                        />
                        <Typography variant='h5'>
                          {t('myTeam.privateNotes')}
                        </Typography>
                      </RowStack>
                      <LiveNote
                        isClosed={isClosed}
                        connection={connection}
                        notes={privateNotes}
                        handleUpdateNotes={n => handleUpdateEvent('notes', n)}
                        isUserOwnedNote={true}
                        isPrivateNote
                      />
                    </InCardStack>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </InCardStack>
        </Container>
      )}
    </MyTeamPage>
  )
}

export default UserMeeting
